import { motion } from "framer-motion";
import { Trash, PencilSimple, Barbell, UploadSimple, Play, ImageSquare } from "@phosphor-icons/react";

/** Compact card for the exercise library. The media is **not** loaded inline
 *  anymore to keep the grid light; users click the small thumbnail/play
 *  trigger (or the row itself) to open the full preview lightbox. Coaches
 *  upload/delete media via the same icon (when empty) or via the hover
 *  toolbar on the right (when present). */
export default function ExerciseCard({
  ex,
  index,
  mediaBlobUrl,
  uploading,
  expanded,
  onToggleExpand,
  onUploadMedia,
  onDeleteMedia,
  onEdit,
  onDelete,
  onPreview,
}) {
  const secondaries = Array.isArray(ex.secondary_muscle_groups) && ex.secondary_muscle_groups.length > 0
    ? ex.secondary_muscle_groups
    : (ex.secondary_muscle_group ? [ex.secondary_muscle_group] : []);
  const hasMedia = !!ex.media_path;
  const isUploading = uploading === ex.id;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.03 }}
      className="bg-[#171717] border border-[#262626] rounded-lg overflow-hidden hover:border-[#333] transition-colors group p-4"
      data-testid={`exercise-lib-${ex.id}`}
    >
      <div className="flex items-start justify-between mb-2 gap-2">
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          {/* Smart trigger: thumbnail if media exists, upload otherwise.
              Compact (28x28) to match the previous layout density. */}
          {hasMedia ? (
            <button
              onClick={() => onPreview(ex)}
              className="relative w-7 h-7 rounded-md overflow-hidden flex-shrink-0 group/media border border-[#D4FF00]/20 hover:border-[#D4FF00]/60 transition-colors"
              title="Mostra anteprima"
              data-testid={`preview-media-${ex.id}`}
            >
              {mediaBlobUrl ? (
                <img src={mediaBlobUrl} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-[#D4FF00]/15 flex items-center justify-center">
                  <ImageSquare size={14} weight="fill" className="text-[#D4FF00]" />
                </div>
              )}
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/media:opacity-100 transition-opacity flex items-center justify-center">
                <Play size={12} weight="fill" className="text-white" />
              </div>
            </button>
          ) : (
            <label
              className="relative w-7 h-7 rounded-md bg-[#007AFF]/15 flex items-center justify-center cursor-pointer flex-shrink-0 hover:bg-[#007AFF]/25 transition-colors"
              title="Carica una foto o una GIF"
              data-testid={`upload-media-label-${ex.id}`}
            >
              <input
                type="file"
                accept="image/*,.gif"
                className="hidden"
                onChange={(e) => {
                  if (e.target.files?.[0]) onUploadMedia(ex.id, e.target.files[0]);
                  e.target.value = "";
                }}
                data-testid={`upload-media-${ex.id}`}
              />
              {isUploading ? (
                <div className="w-3.5 h-3.5 border-2 border-[#007AFF] border-t-transparent rounded-full animate-spin" />
              ) : (
                <UploadSimple size={14} className="text-[#007AFF]" />
              )}
            </label>
          )}

          <button
            onClick={() => onToggleExpand(ex.id)}
            className="flex items-center gap-2 text-left min-w-0 flex-1"
          >
            <Barbell size={12} weight="fill" className="text-zinc-600 flex-shrink-0" />
            <h4 className="font-semibold text-white text-sm truncate">{ex.name}</h4>
          </button>
        </div>

        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
          {hasMedia && (
            <button
              onClick={() => onDeleteMedia(ex.id)}
              className="w-6 h-6 rounded flex items-center justify-center text-zinc-600 hover:text-[#FF9500] hover:bg-[#FF9500]/10 transition-all"
              title="Elimina foto/GIF"
              data-testid={`delete-media-${ex.id}`}
            >
              <ImageSquare size={14} />
            </button>
          )}
          <button
            onClick={() => onEdit(ex)}
            className="w-6 h-6 rounded flex items-center justify-center text-zinc-600 hover:text-[#007AFF] hover:bg-[#007AFF]/10 transition-all"
            data-testid={`edit-exercise-lib-${ex.id}`}
          >
            <PencilSimple size={14} />
          </button>
          <button
            onClick={() => onDelete(ex.id)}
            className="w-6 h-6 rounded flex items-center justify-center text-zinc-600 hover:text-red-400 hover:bg-red-500/10 transition-all"
            data-testid={`delete-exercise-lib-${ex.id}`}
          >
            <Trash size={14} />
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {ex.category && <span className="text-[10px] px-1.5 py-0.5 bg-[#007AFF]/10 text-[#007AFF] rounded-full font-medium">{ex.category}</span>}
        {ex.muscle_group && <span className="text-[10px] px-1.5 py-0.5 bg-[#D4FF00]/10 text-[#D4FF00] rounded-full font-medium">{ex.muscle_group}</span>}
        {secondaries.map((sg) => (
          <span key={`sg-${sg}`} className="text-[10px] px-1.5 py-0.5 bg-[#34C759]/10 text-[#34C759] rounded-full font-medium">{sg}</span>
        ))}
        {ex.equipment && <span className="text-[10px] px-1.5 py-0.5 bg-zinc-700/50 text-zinc-400 rounded-full font-medium">{ex.equipment}</span>}
      </div>

      {ex.description && !expanded && (
        <p
          className="text-xs text-zinc-600 mt-2 line-clamp-1 cursor-pointer hover:text-zinc-400 transition-colors"
          onClick={() => onToggleExpand(ex.id)}
        >
          {ex.description}
        </p>
      )}
      {ex.description && expanded && (
        <div className="mt-3 p-3 bg-[#0A0A0A] rounded-lg border border-[#1E1E1E]">
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider font-bold mb-1">Descrizione</p>
          <p className="text-xs text-zinc-300 whitespace-pre-wrap leading-relaxed">{ex.description}</p>
        </div>
      )}
    </motion.div>
  );
}
